import React, { useState, useEffect, useCallback } from 'react';
import { HardHat, Plus, Search, MapPin, Calendar, IndianRupee, Users, ChevronRight, ArrowLeft, Pencil, Trash2, CheckCircle2, Clock, AlertTriangle, Pause, ListChecks, Flag, Camera, Receipt, X, Save, Truck } from 'lucide-react';
import { siteProjectAPI, customerAPI, unwrapItems, fetchPaged } from '../../services/api';
import api from '../../services/api';
import Pagination from '../ui/Pagination';
import { WorkDetailsTab } from './WorkDetailsTab';
import { ServiceInvoiceTab } from './ServiceInvoiceTab';
import { LabourInvoiceTab } from './LabourInvoiceTab';
import { TransportationTab } from './TransportationTab';
import { ProjectAnalytics } from './ProjectAnalytics';
import CustomerSearchInput from '../ui/CustomerSearchInput';
import ProductSearchInput from '../ui/ProductSearchInput';

const STATUS_CONFIG = {
  'Planning': { color: 'bg-blue-100 text-blue-700', icon: Clock },
  'In Progress': { color: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  'On Hold': { color: 'bg-gray-100 text-gray-600', icon: Pause },
  'Completed': { color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: HardHat },
  { id: 'materials', label: 'Material Procurement', icon: Receipt },
  { id: 'work-items', label: 'Work Details', icon: ListChecks },
  { id: 'service-invoice', label: 'Service Invoice', icon: Flag },
  { id: 'labour-invoice', label: 'Labour Invoice', icon: Users },
  { id: 'transportation', label: 'Transportation', icon: Truck },
  { id: 'expenses', label: 'Expenses', icon: Receipt },
  { id: 'photos', label: 'Photos', icon: Camera },
];

const PROJECT_TYPES = [
  'Thermoplastic Roadmarking',
  'Kerb Marking',
  'Cold Plastic',
  'Signages',
  'Road Studs',
  'Crash Barrier',
  'Other Road Safety Products',
];

const EXPENSE_CATEGORIES = ['Supervisor Salary', 'Rent', 'Equipment', 'Miscellaneous'];

// Default unit hint based on project type (used next to Estimated Work input)
const workUnitFor = (projectType) => {
  switch (projectType) {
    case 'Thermoplastic Roadmarking':
    case 'Kerb Marking':
    case 'Cold Plastic': return 'sqm';
    case 'Crash Barrier': return 'mtr';
    case 'Signages':
    case 'Road Studs':
    case 'Other Road Safety Products': return 'pcs';
    default: return '';
  }
};

const formatCurrency = (v) => '₹' + (v || 0).toLocaleString('en-IN');
const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

// ============ STATS BAR ============
const StatsBar = ({ stats }) => {
  const cards = [
    { label: 'Total', value: stats.total, color: '#24549c' },
    { label: 'In Progress', value: stats.in_progress, color: '#d97706' },
    { label: 'Planning', value: stats.planning, color: '#3b82f6' },
    { label: 'Completed', value: stats.completed, color: '#059669' },
    { label: 'Budget', value: formatCurrency(stats.total_budget), color: '#7c3aed' },
    { label: 'Spent', value: formatCurrency(stats.total_spent), color: '#dc2626' },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
      {cards.map(c => (
        <div key={c.label} className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{c.label}</p>
          <p className="text-lg font-bold mt-0.5" style={{ color: c.color }}>{c.value}</p>
        </div>
      ))}
    </div>
  );
};

// ============ PORTFOLIO KPI STRIP (P2) ============
// Shows enriched per-project metrics under each project card on the Portfolio list.
const PortfolioKpiStrip = ({ project }) => {
  const fmtMoney = (n) => '₹' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const workByUnit = project.work_done_by_unit || {};
  const workEntries = Object.entries(workByUnit).filter(([, qty]) => Number(qty) > 0);
  const workDoneLabel = workEntries.length === 0
    ? '—'
    : workEntries.map(([u, q]) => `${Number(q).toLocaleString('en-IN', { maximumFractionDigits: 2 })} ${u}`).join(' · ');

  const cells = [
    { key: 'work', label: 'Work Done', value: workDoneLabel, tone: 'text-blue-700', bg: 'bg-blue-50/50' },
    { key: 'material', label: 'Material Used', value: fmtMoney(project.material_consumed_value), tone: 'text-red-600', bg: 'bg-red-50/40' },
    { key: 'service', label: 'Service Inv', value: fmtMoney(project.service_invoice_total), tone: 'text-emerald-700', bg: 'bg-emerald-50/40' },
    { key: 'labour', label: 'Labour', value: fmtMoney(project.labour_invoice_total), tone: 'text-amber-700', bg: 'bg-amber-50/40' },
    { key: 'transport', label: 'Transport', value: fmtMoney(project.transportation_total), tone: 'text-indigo-700', bg: 'bg-indigo-50/40' },
    { key: 'expenses', label: 'Expenses', value: fmtMoney(project.expenses_total), tone: 'text-purple-700', bg: 'bg-purple-50/40' },
  ];
  return (
    <div className="mt-3 pt-3 border-t border-gray-50 grid grid-cols-3 md:grid-cols-6 gap-2" data-testid={`portfolio-kpi-strip-${project.id}`}>
      {cells.map(c => (
        <div key={c.key} className={`px-2 py-1.5 rounded-md ${c.bg}`} data-testid={`portfolio-kpi-${c.key}-${project.id}`}>
          <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">{c.label}</p>
          <p className={`text-xs font-bold truncate ${c.tone}`} title={c.value}>{c.value}</p>
        </div>
      ))}
    </div>
  );
};

// ============ PROJECT FORM ============
const ProjectForm = ({ project, onSave, onCancel, customers }) => {
  const [form, setForm] = useState(project || {
    project_name: '', project_type: '', description: '', customer_id: '', customer_name: '',
    location: '', site_address: '', stretch: '', start_date: '', end_date: '', budget: 0, assigned_to: '', status: 'Planning',
    estimated_work: 0, estimated_work_unit: '', rate_per_unit: 0, labour_rate_per_unit: 0, avg_material_consumption_per_unit: 0,
  });

  // Auto-sync the unit hint when project type changes (non-destructive — user can still override)
  useEffect(() => {
    if (form.project_type && !form.estimated_work_unit) {
      const hint = workUnitFor(form.project_type);
      if (hint) setForm(f => ({ ...f, estimated_work_unit: hint }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.project_type]);

  const handleCustomerSelect = (cust) => {
    setForm(f => ({
      ...f,
      customer_id: cust?.id || '',
      customer_name: cust?.name || '',
      customer_address: cust?.address || '',
      customer_city: cust?.city || '',
      customer_state: cust?.state || '',
      customer_pincode: cust?.pincode || '',
      customer_gstin: cust?.gstin || '',
      customer_phone: cust?.phone || '',
      customer_email: cust?.email || '',
      customer_contact_person: cust?.contact_person || cust?.contact || '',
    }));
  };

  const handleCustomerClear = () => {
    setForm(f => ({
      ...f,
      customer_id: '', customer_name: '', customer_address: '', customer_city: '',
      customer_state: '', customer_pincode: '', customer_gstin: '',
      customer_phone: '', customer_email: '', customer_contact_person: '',
    }));
  };

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6">
      <h3 className="text-base font-bold text-gray-900 mb-4">{project ? 'Edit Project' : 'New Site Project'}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Project Name *</label>
          <input data-testid="project-name-input" value={form.project_name} onChange={e => setForm(f => ({ ...f, project_name: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" placeholder="e.g. NHAI NH-48 Signage" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Project Type</label>
          <select data-testid="project-type-select" value={form.project_type} onChange={e => setForm(f => ({ ...f, project_type: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200">
            <option value="">Select Type</option>
            {PROJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Customer</label>
          <CustomerSearchInput
            value={form.customer_name}
            customerId={form.customer_id}
            onSelect={handleCustomerSelect}
            onClear={handleCustomerClear}
            testId="project-customer-search"
            placeholder="Search customer by name, GSTIN, or email..."
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Assigned To</label>
          <input data-testid="project-assigned-input" value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" placeholder="Project manager name" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
          <input data-testid="project-location-input" value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" placeholder="City / Region" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Stretch / Chainage</label>
          <input data-testid="project-stretch-input" value={form.stretch} onChange={e => setForm(f => ({ ...f, stretch: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" placeholder="e.g. KM 120-145" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Site Address</label>
          <input data-testid="project-address-input" value={form.site_address} onChange={e => setForm(f => ({ ...f, site_address: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" placeholder="Full site address" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
          <textarea data-testid="project-description-input" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" placeholder="Project details..." />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
          <input data-testid="project-start-date" type="date" value={form.start_date || ''} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
          <input data-testid="project-end-date" type="date" value={form.end_date || ''} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Budget (₹)</label>
          <input data-testid="project-budget-input" type="number" value={form.budget} onChange={e => setForm(f => ({ ...f, budget: parseFloat(e.target.value) || 0 }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
        </div>
        {/* Phase 1 — optional estimation inputs (used by Work Details auto-calc in later phases) */}
        <div className="md:col-span-2 border-t border-gray-100 pt-4 mt-1">
          <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Estimation <span className="text-[10px] font-normal text-gray-400 normal-case">(all optional — can be updated later)</span></p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Estimated Work {form.estimated_work_unit && <span className="text-gray-400">({form.estimated_work_unit})</span>}</label>
              <div className="flex gap-2">
                <input data-testid="project-estimated-work" type="number" value={form.estimated_work || ''} onChange={e => setForm(f => ({ ...f, estimated_work: parseFloat(e.target.value) || 0 }))} placeholder="e.g. 5000" className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
                <input data-testid="project-estimated-work-unit" value={form.estimated_work_unit || ''} onChange={e => setForm(f => ({ ...f, estimated_work_unit: e.target.value }))} placeholder={workUnitFor(form.project_type) || 'unit'} className="w-20 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Rate / Unit (₹)</label>
              <input data-testid="project-rate-per-unit" type="number" value={form.rate_per_unit || ''} onChange={e => setForm(f => ({ ...f, rate_per_unit: parseFloat(e.target.value) || 0 }))} placeholder="Contracted sale rate" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Labour Rate / Unit (₹)</label>
              <input data-testid="project-labour-rate-per-unit" type="number" value={form.labour_rate_per_unit || ''} onChange={e => setForm(f => ({ ...f, labour_rate_per_unit: parseFloat(e.target.value) || 0 }))} placeholder="Used to auto-calc labour charge" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Avg Material Consumption / Unit</label>
              <input data-testid="project-avg-material-consumption" type="number" value={form.avg_material_consumption_per_unit || ''} onChange={e => setForm(f => ({ ...f, avg_material_consumption_per_unit: parseFloat(e.target.value) || 0 }))} placeholder="Reference for 'Above/Below Avg' tag" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
            </div>
          </div>
        </div>
        {project && (
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select data-testid="project-status-select" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200">
              {['Planning', 'In Progress', 'On Hold', 'Completed'].map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        )}
      </div>
      <div className="flex justify-end gap-2 mt-6">
        <button data-testid="project-cancel-btn" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
        <button data-testid="project-save-btn" onClick={() => onSave(form)} disabled={!form.project_name} className="px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>
          <Save className="w-4 h-4 inline mr-1" />{project ? 'Update' : 'Create'}
        </button>
      </div>
    </div>
  );
};

// ============ WORK ITEMS TAB ============
// ============ MATERIAL COSTS TAB ============
const OpeningStockDialog = ({ projectId, onClose, onSaved }) => {
  const [fyLabel, setFyLabel] = useState('FY 2024-25');
  const [asOfDate, setAsOfDate] = useState(new Date().toISOString().slice(0, 10));
  const [remarks, setRemarks] = useState('');
  const [items, setItems] = useState([{ product_name: '', qty: 0, unit: 'Kg', rate: 0, amount: 0, hsn_code: '', description: '' }]);
  const [saving, setSaving] = useState(false);

  const updateItem = (i, field, value) => {
    setItems(prev => prev.map((it, idx) => {
      if (idx !== i) return it;
      const next = { ...it, [field]: value };
      if (field === 'qty' || field === 'rate') {
        next.amount = (Number(next.qty) || 0) * (Number(next.rate) || 0);
      }
      return next;
    }));
  };
  const total = items.reduce((s, it) => s + (Number(it.amount) || 0), 0);

  const handleSave = async () => {
    if (!items.some(it => it.product_name && Number(it.qty) > 0)) {
      alert('Please add at least one product with qty > 0'); return;
    }
    try {
      setSaving(true);
      await api.post(`/site-projects/${projectId}/opening-stock`, {
        fy_label: fyLabel.trim(),
        as_of_date: asOfDate,
        remarks: remarks.trim(),
        items: items.filter(it => it.product_name && Number(it.qty) > 0),
      });
      onSaved();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save opening stock');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" data-testid="opening-stock-dialog">
        <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
            <Receipt className="w-4 h-4 text-emerald-600" /> Add Opening Stock (Carry-Forward)
          </h3>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-semibold text-gray-600 mb-1 block">FY Label</label>
              <input type="text" value={fyLabel} onChange={e => setFyLabel(e.target.value)} placeholder="FY 2024-25"
                data-testid="opening-fy-label" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="text-[11px] font-semibold text-gray-600 mb-1 block">As-of Date</label>
              <input type="date" value={asOfDate} onChange={e => setAsOfDate(e.target.value)}
                data-testid="opening-as-of-date" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] font-semibold text-gray-600">Items at site (Carry-Forward)</label>
              <button onClick={() => setItems(prev => [...prev, { product_name: '', qty: 0, unit: 'Kg', rate: 0, amount: 0, hsn_code: '', description: '' }])}
                data-testid="opening-add-item-btn" className="text-[11px] font-semibold text-blue-600 hover:underline flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add Row
              </button>
            </div>
            <div className="overflow-x-auto border border-gray-100 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-gray-50"><tr>
                  {['Product', 'Description', 'Qty', 'Unit', 'Rate', 'Amount', ''].map(h => (
                    <th key={h} className="text-left px-2 py-1.5 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>
                  ))}
                </tr></thead>
                <tbody>{items.map((it, i) => (
                  <tr key={i} className="border-t border-gray-50">
                    <td className="px-2 py-1 min-w-[180px]"><ProductSearchInput
                      value={it.product_name}
                      onChange={(v) => updateItem(i, 'product_name', v)}
                      onSelect={(p) => {
                        // Auto-fill name + unit + rate (selling_price) + HSN from product master.
                        setItems(prev => prev.map((row, idx) => {
                          if (idx !== i) return row;
                          const next = {
                            ...row,
                            product_name: p.name || '',
                            unit: p.unit || row.unit || 'Kg',
                            rate: Number(p.selling_price) || row.rate || 0,
                            hsn_code: p.hsn_code || row.hsn_code || '',
                          };
                          next.amount = (Number(next.qty) || 0) * (Number(next.rate) || 0);
                          return next;
                        }));
                      }}
                      onClear={() => updateItem(i, 'product_name', '')}
                      placeholder="Search product…"
                      testId={`opening-product-search-${i}`}
                      showMeta={false}
                    /></td>
                    <td className="px-2 py-1"><input type="text" value={it.description} onChange={e => updateItem(i, 'description', e.target.value)} className="w-full px-1.5 py-1 text-xs border border-gray-200 rounded" /></td>
                    <td className="px-2 py-1 w-20"><input type="number" value={it.qty} onChange={e => updateItem(i, 'qty', e.target.value)} className="w-full px-1.5 py-1 text-xs border border-gray-200 rounded text-right" /></td>
                    <td className="px-2 py-1 w-20"><input type="text" value={it.unit} onChange={e => updateItem(i, 'unit', e.target.value)} className="w-full px-1.5 py-1 text-xs border border-gray-200 rounded" /></td>
                    <td className="px-2 py-1 w-24"><input type="number" value={it.rate} onChange={e => updateItem(i, 'rate', e.target.value)} className="w-full px-1.5 py-1 text-xs border border-gray-200 rounded text-right" /></td>
                    <td className="px-2 py-1 w-28 font-bold text-right" style={{ color: '#24549c' }}>{formatCurrency(it.amount)}</td>
                    <td className="px-2 py-1"><button onClick={() => setItems(prev => prev.filter((_, idx) => idx !== i))} disabled={items.length === 1}
                      className="p-1 hover:bg-red-50 rounded disabled:opacity-40"><Trash2 className="w-3 h-3 text-red-500" /></button></td>
                  </tr>
                ))}</tbody>
                <tfoot className="bg-emerald-50">
                  <tr><td colSpan="5" className="px-2 py-2 text-right text-[11px] font-bold text-gray-700">Total Carry-Forward Value:</td>
                  <td className="px-2 py-2 text-right text-sm font-extrabold text-emerald-700">{formatCurrency(total)}</td><td></td></tr>
                </tfoot>
              </table>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-semibold text-gray-600 mb-1 block">Remarks</label>
            <textarea value={remarks} onChange={e => setRemarks(e.target.value)} rows="2" placeholder="e.g. Verified physical stock at site dated 2025-04-01"
              data-testid="opening-remarks" className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2 bg-gray-50 sticky bottom-0">
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 text-xs font-semibold text-gray-600 border border-gray-200 rounded-lg hover:bg-white">Cancel</button>
          <button onClick={handleSave} disabled={saving}
            data-testid="opening-save-btn"
            className="px-4 py-2 text-xs font-semibold text-white rounded-lg flex items-center gap-1.5 disabled:opacity-50"
            style={{ backgroundColor: '#24549c' }}>
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save Opening Stock'}
          </button>
        </div>
      </div>
    </div>
  );
};

const MaterialCostsTab = ({ project }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOpeningDialog, setShowOpeningDialog] = useState(false);
  const refetch = useCallback(() => {
    setLoading(true);
    api.get(`/site-projects/${project.id}/material-costs`)
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [project.id]);
  useEffect(() => { refetch(); }, [refetch]);

  if (loading) return <div className="flex justify-center py-12"><Clock className="w-5 h-5 animate-spin text-blue-600" /></div>;
  if (!data || data.total_dispatches === 0) return (
    <>
      <div className="text-center py-12 text-gray-400" data-testid="material-empty-state">
        <Receipt className="w-10 h-10 mx-auto mb-2 text-gray-300" />
        <p className="text-sm">No material procurement yet</p>
        <p className="text-[11px] mt-1">Material procurement will appear here when OCs linked to this project are dispatched</p>
        <button onClick={() => setShowOpeningDialog(true)}
          data-testid="add-opening-stock-empty-btn"
          className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white rounded-lg"
          style={{ backgroundColor: '#24549c' }}>
          <Plus className="w-3.5 h-3.5" /> Add Opening Stock (Carry-Forward)
        </button>
      </div>
      {showOpeningDialog && (
        <OpeningStockDialog projectId={project.id} onClose={() => setShowOpeningDialog(false)}
          onSaved={() => { setShowOpeningDialog(false); refetch(); }} />
      )}
    </>
  );

  const openingDispatches = (data.dispatches || []).filter(d => d.is_opening_stock);
  const realDispatches = (data.dispatches || []).filter(d => !d.is_opening_stock);

  return (
    <div className="space-y-4" data-testid="material-costs-tab">
      {/* Header action */}
      <div className="flex items-center justify-end">
        <button onClick={() => setShowOpeningDialog(true)}
          data-testid="add-opening-stock-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-emerald-700 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100">
          <Plus className="w-3.5 h-3.5" /> Add Opening Stock (Carry-Forward)
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase">Total Material Cost</p>
          <p className="text-lg font-bold text-red-600">{formatCurrency(data.total_material_cost)}</p>
        </div>
        <div className="bg-white rounded-xl border border-emerald-100 p-3">
          <p className="text-[10px] font-semibold text-emerald-500 uppercase">Carry-Forward</p>
          <p className="text-lg font-bold text-emerald-700">{formatCurrency(data.opening_stock_value || 0)}</p>
          <p className="text-[10px] text-gray-400">{data.opening_stock_count || 0} entry(ies)</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase">Real Dispatches</p>
          <p className="text-lg font-bold" style={{ color: '#24549c' }}>{(data.total_dispatches || 0) - (data.opening_stock_count || 0)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase">Products</p>
          <p className="text-lg font-bold text-emerald-600">{data.product_breakdown?.length || 0}</p>
        </div>
      </div>

      {/* Opening Stock section (if any) */}
      {openingDispatches.length > 0 && (
        <div className="bg-white rounded-xl border-2 border-emerald-100 overflow-hidden" data-testid="opening-stock-section">
          <div className="px-4 py-3 border-b border-emerald-100 bg-emerald-50 flex items-center gap-2">
            <Receipt className="w-3.5 h-3.5 text-emerald-600" />
            <h3 className="text-xs font-bold text-emerald-800">Opening Stock (Carry-Forward from previous FY)</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-gray-50">
                {['FY', 'As-of Date', 'Items', 'Value', 'Remarks', 'Added By'].map(h => (
                  <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>
                ))}
              </tr></thead>
              <tbody>{openingDispatches.map((d, i) => (
                <tr key={i} className="border-t border-gray-50">
                  <td className="px-4 py-2 font-semibold text-emerald-700">{d.fy_label || '—'}</td>
                  <td className="px-4 py-2 font-mono">{d.dispatched_at?.slice(0, 10)}</td>
                  <td className="px-4 py-2 text-gray-600">{(d.items || []).map(it => `${it.product_name} (${it.qty} ${it.unit})`).join(', ')}</td>
                  <td className="px-4 py-2 font-bold text-emerald-700">{formatCurrency(d.total_value)}</td>
                  <td className="px-4 py-2 text-gray-500 max-w-xs truncate">{d.remarks || '—'}</td>
                  <td className="px-4 py-2 text-gray-500">{d.dispatched_by || '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}

      {showOpeningDialog && (
        <OpeningStockDialog projectId={project.id} onClose={() => setShowOpeningDialog(false)}
          onSaved={() => { setShowOpeningDialog(false); refetch(); }} />
      )}

      {/* Product Breakdown */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100"><h3 className="text-xs font-bold text-gray-900">Product-wise Material Cost</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="bg-gray-50">
              {['Product', 'Total Qty', 'Unit', 'Total Amount', 'Dispatches'].map(h => (
                <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>
              ))}
            </tr></thead>
            <tbody>{(data.product_breakdown || []).map((p, i) => (
              <tr key={i} className="border-t border-gray-50 hover:bg-gray-50/50">
                <td className="px-4 py-2 font-semibold text-gray-900">{p.product_name}</td>
                <td className="px-4 py-2 font-mono">{p.total_qty.toLocaleString()}</td>
                <td className="px-4 py-2 text-gray-500">{p.unit}</td>
                <td className="px-4 py-2 font-bold" style={{ color: '#24549c' }}>{formatCurrency(p.total_amount)}</td>
                <td className="px-4 py-2 text-gray-500">{p.dispatches}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>

      {/* Dispatch History */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100"><h3 className="text-xs font-bold text-gray-900">Dispatch History</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="bg-gray-50">
              {['OC #', 'Invoice #', 'Vehicle', 'Items', 'Value', 'Dispatched', 'By'].map(h => (
                <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>
              ))}
            </tr></thead>
            <tbody>{(data.dispatches || []).map((d, i) => (
              <tr key={i} className="border-t border-gray-50">
                <td className="px-4 py-2 font-mono font-semibold">{d.oc_number}</td>
                <td className="px-4 py-2 font-mono text-blue-600">{d.invoice_number || '-'}</td>
                <td className="px-4 py-2 text-gray-600">{d.vehicle_number}</td>
                <td className="px-4 py-2">{d.items?.length || 0} items</td>
                <td className="px-4 py-2 font-bold">{formatCurrency(d.total_value)}</td>
                <td className="px-4 py-2 text-gray-500">{formatDate(d.dispatched_at)}</td>
                <td className="px-4 py-2 text-gray-500">{d.dispatched_by}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const WorkItemsTab = ({ project, onUpdate }) => {
  const [items, setItems] = useState(project.work_items || []);
  const [newItem, setNewItem] = useState('');
  const [saving, setSaving] = useState(false);

  const addItem = () => {
    if (!newItem.trim()) return;
    setItems([...items, { id: crypto.randomUUID(), title: newItem.trim(), description: '', status: 'Pending', assigned_to: '', due_date: null }]);
    setNewItem('');
  };

  const toggleStatus = (id) => {
    setItems(items.map(i => i.id === id ? { ...i, status: i.status === 'Completed' ? 'Pending' : 'Completed', completed_at: i.status !== 'Completed' ? new Date().toISOString() : null } : i));
  };

  const removeItem = (id) => setItems(items.filter(i => i.id !== id));

  const save = async () => {
    setSaving(true);
    try { await onUpdate(items); } finally { setSaving(false); }
  };

  const completed = items.filter(i => i.status === 'Completed').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-bold text-gray-900">Work Items</h4>
          <p className="text-xs text-gray-400">{completed}/{items.length} completed</p>
        </div>
        <button data-testid="save-work-items-btn" onClick={save} disabled={saving} className="px-3 py-1.5 text-xs text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
      {items.length > 0 && (
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div className="h-2 rounded-full transition-all" style={{ width: `${items.length > 0 ? (completed / items.length) * 100 : 0}%`, backgroundColor: '#059669' }} />
        </div>
      )}
      <div className="space-y-2">
        {items.map(item => (
          <div key={item.id} className={`flex items-center gap-3 p-3 rounded-lg border ${item.status === 'Completed' ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-gray-100'}`}>
            <button data-testid={`toggle-item-${item.id}`} onClick={() => toggleStatus(item.id)} className="flex-shrink-0">
              <CheckCircle2 className={`w-5 h-5 ${item.status === 'Completed' ? 'text-emerald-500' : 'text-gray-300'}`} />
            </button>
            <span className={`flex-1 text-sm ${item.status === 'Completed' ? 'line-through text-gray-400' : 'text-gray-800'}`}>{item.title}</span>
            <button onClick={() => removeItem(item.id)} className="text-gray-300 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input data-testid="new-work-item-input" value={newItem} onChange={e => setNewItem(e.target.value)} onKeyDown={e => e.key === 'Enter' && addItem()} placeholder="Add a work item..." className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
        <button data-testid="add-work-item-btn" onClick={addItem} className="px-3 py-2 text-sm text-white rounded-lg" style={{ backgroundColor: '#24549c' }}><Plus className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

// ============ MILESTONES TAB ============
const MilestonesTab = ({ project, onUpdate }) => {
  const [milestones, setMilestones] = useState(project.milestones || []);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', target_date: '' });
  const [saving, setSaving] = useState(false);

  const addMilestone = () => {
    if (!form.title.trim()) return;
    setMilestones([...milestones, { id: crypto.randomUUID(), ...form, status: 'Pending' }]);
    setForm({ title: '', description: '', target_date: '' });
    setShowForm(false);
  };

  const toggleAchieved = (id) => {
    setMilestones(milestones.map(m => m.id === id ? { ...m, status: m.status === 'Achieved' ? 'Pending' : 'Achieved', achieved_at: m.status !== 'Achieved' ? new Date().toISOString() : null } : m));
  };

  const save = async () => {
    setSaving(true);
    try { await onUpdate(milestones); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-bold text-gray-900">Milestones</h4>
        <div className="flex gap-2">
          <button data-testid="save-milestones-btn" onClick={save} disabled={saving} className="px-3 py-1.5 text-xs text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>{saving ? 'Saving...' : 'Save'}</button>
          <button data-testid="add-milestone-btn" onClick={() => setShowForm(!showForm)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50"><Plus className="w-3.5 h-3.5 inline mr-1" />Add</button>
        </div>
      </div>
      {showForm && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 space-y-3">
          <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Milestone title..." className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description (optional)" className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <div className="flex gap-2 items-center">
            <input type="date" value={form.target_date} onChange={e => setForm(f => ({ ...f, target_date: e.target.value }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            <button onClick={addMilestone} disabled={!form.title.trim()} className="px-3 py-2 text-xs text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>Add</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {milestones.map((m, i) => (
          <div key={m.id} className={`flex items-start gap-3 p-3 rounded-lg border ${m.status === 'Achieved' ? 'bg-emerald-50 border-emerald-100' : 'bg-white border-gray-100'}`}>
            <button onClick={() => toggleAchieved(m.id)} className="mt-0.5"><Flag className={`w-4 h-4 ${m.status === 'Achieved' ? 'text-emerald-500 fill-emerald-500' : 'text-gray-300'}`} /></button>
            <div className="flex-1">
              <p className={`text-sm font-medium ${m.status === 'Achieved' ? 'line-through text-gray-400' : 'text-gray-800'}`}>{m.title}</p>
              {m.description && <p className="text-xs text-gray-400 mt-0.5">{m.description}</p>}
              {m.target_date && <p className="text-[10px] text-gray-400 mt-1"><Calendar className="w-3 h-3 inline mr-1" />{formatDate(m.target_date)}</p>}
            </div>
            <button onClick={() => setMilestones(milestones.filter(x => x.id !== m.id))} className="text-gray-300 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {milestones.length === 0 && <p className="text-xs text-gray-400 text-center py-6">No milestones added yet</p>}
      </div>
    </div>
  );
};

// ============ EXPENSES TAB ============
const ExpensesTab = ({ project, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ description: '', amount: '', category: '', date: '' });
  const [saving, setSaving] = useState(false);

  const addExpense = async () => {
    if (!form.description || !form.amount) return;
    setSaving(true);
    try {
      await siteProjectAPI.addExpense(project.id, { ...form, amount: parseFloat(form.amount) || 0 });
      setForm({ description: '', amount: '', category: '', date: '' });
      setShowForm(false);
      onRefresh();
    } finally { setSaving(false); }
  };

  const deleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    await siteProjectAPI.deleteExpense(project.id, expenseId);
    onRefresh();
  };

  const totalExpenses = (project.expenses || []).reduce((sum, e) => sum + (e.amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-bold text-gray-900">Expenses</h4>
          <p className="text-xs text-gray-400">Total: {formatCurrency(totalExpenses)} / Budget: {formatCurrency(project.budget)}</p>
        </div>
        <button data-testid="add-expense-btn" onClick={() => setShowForm(!showForm)} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-gray-50"><Plus className="w-3.5 h-3.5 inline mr-1" />Add Expense</button>
      </div>
      {project.budget > 0 && (
        <div className="w-full bg-gray-100 rounded-full h-2">
          <div className="h-2 rounded-full transition-all" style={{ width: `${Math.min(100, (totalExpenses / project.budget) * 100)}%`, backgroundColor: totalExpenses > project.budget ? '#dc2626' : '#3b82f6' }} />
        </div>
      )}
      {showForm && (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-3">
          <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description *" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} placeholder="Amount (₹) *" className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg">
            <option value="">Category</option>
            {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} className="px-3 py-2 text-sm border border-gray-200 rounded-lg" />
          <div className="md:col-span-2 flex justify-end gap-2">
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded-lg">Cancel</button>
            <button data-testid="save-expense-btn" onClick={addExpense} disabled={saving || !form.description || !form.amount} className="px-3 py-1.5 text-xs text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}>{saving ? 'Adding...' : 'Add'}</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {(project.expenses || []).map(exp => (
          <div key={exp.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
            <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0"><IndianRupee className="w-4 h-4 text-blue-600" /></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-800 truncate">{exp.description}</p>
              <p className="text-[10px] text-gray-400">{exp.category && `${exp.category} · `}{formatDate(exp.date)} · by {exp.added_by}</p>
            </div>
            <span className="text-sm font-bold text-gray-900">{formatCurrency(exp.amount)}</span>
            <button onClick={() => deleteExpense(exp.id)} className="text-gray-300 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
        ))}
        {(project.expenses || []).length === 0 && <p className="text-xs text-gray-400 text-center py-6">No expenses recorded yet</p>}
      </div>
    </div>
  );
};

// ============ PHOTOS TAB ============
const PhotosTab = ({ project, onRefresh }) => {
  const [url, setUrl] = useState('');
  const [caption, setCaption] = useState('');

  const addPhoto = async () => {
    if (!url.trim()) return;
    await siteProjectAPI.addPhoto(project.id, { url, caption });
    setUrl(''); setCaption('');
    onRefresh();
  };

  const deletePhoto = async (photoId) => {
    if (!window.confirm('Delete this photo?')) return;
    await siteProjectAPI.deletePhoto(project.id, photoId);
    onRefresh();
  };

  return (
    <div className="space-y-4">
      <h4 className="text-sm font-bold text-gray-900">Site Photos</h4>
      <div className="flex gap-2">
        <input data-testid="photo-url-input" value={url} onChange={e => setUrl(e.target.value)} placeholder="Paste image URL..." className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
        <input value={caption} onChange={e => setCaption(e.target.value)} placeholder="Caption (optional)" className="w-40 px-3 py-2 text-sm border border-gray-200 rounded-lg" />
        <button data-testid="add-photo-btn" onClick={addPhoto} disabled={!url.trim()} className="px-3 py-2 text-sm text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#24549c' }}><Plus className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {(project.photos || []).map(photo => (
          <div key={photo.id} className="relative group rounded-lg overflow-hidden border border-gray-100">
            <img src={photo.url} alt={photo.caption} className="w-full h-32 object-cover" onError={e => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="128" fill="%23f3f4f6"><rect width="200" height="128"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%239ca3af" font-size="12">Image Error</text></svg>'; }} />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
              <button onClick={() => deletePhoto(photo.id)} className="opacity-0 group-hover:opacity-100 p-1.5 bg-white rounded-full shadow"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>
            </div>
            {photo.caption && <p className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-[10px] px-2 py-1 truncate">{photo.caption}</p>}
          </div>
        ))}
      </div>
      {(project.photos || []).length === 0 && <p className="text-xs text-gray-400 text-center py-6">No photos uploaded yet</p>}
    </div>
  );
};

// ============ PROJECT DETAIL ============
const ProjectDetail = ({ projectId, onBack }) => {
  const [project, setProject] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [customers, setCustomers] = useState([]);

  const loadProject = useCallback(async () => {
    const data = await siteProjectAPI.getById(projectId);
    setProject(data);
  }, [projectId]);

  useEffect(() => { loadProject(); customerAPI.getAll().then(d => setCustomers(unwrapItems(d))); }, [loadProject]);

  if (!project) return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" /></div>;

  const sc = STATUS_CONFIG[project.status] || STATUS_CONFIG['Planning'];
  const StatusIcon = sc.icon;

  const handleUpdate = async (form) => {
    await siteProjectAPI.update(project.id, form);
    setEditing(false);
    loadProject();
  };

  if (editing) return (
    <div className="p-6">
      <ProjectForm project={project} onSave={handleUpdate} onCancel={() => setEditing(false)} customers={customers} />
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <button data-testid="project-back-btn" onClick={onBack} className="p-1.5 rounded-lg hover:bg-gray-100"><ArrowLeft className="w-4 h-4" /></button>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900">{project.project_name}</h2>
              {project.project_code && <span className="px-2 py-0.5 text-[10px] font-mono font-bold bg-blue-50 text-blue-700 rounded border border-blue-200" data-testid="project-code-badge">{project.project_code}</span>}
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
              {project.customer_name && <span><Users className="w-3 h-3 inline mr-1" />{project.customer_name}</span>}
              {project.location && <span><MapPin className="w-3 h-3 inline mr-1" />{project.location}</span>}
              {project.project_type && <span className="px-1.5 py-0.5 bg-gray-100 rounded text-gray-500">{project.project_type}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${sc.color}`}><StatusIcon className="w-3 h-3 inline mr-1" />{project.status}</span>
          <button data-testid="edit-project-btn" onClick={() => setEditing(true)} className="p-1.5 rounded-lg hover:bg-gray-100"><Pencil className="w-4 h-4 text-gray-400" /></button>
          <button data-testid="delete-project-btn" onClick={async () => { if (window.confirm('Delete this project?')) { await siteProjectAPI.delete(project.id); onBack(); } }} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-400" /></button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-gray-600">Completion</span>
          <span className="text-xs font-bold" style={{ color: '#24549c' }}>{project.completion}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-2.5">
          <div className="h-2.5 rounded-full transition-all" style={{ width: `${project.completion}%`, backgroundColor: '#24549c' }} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 text-center">
          <div><p className="text-[10px] text-gray-400">Budget</p><p className="text-sm font-bold text-gray-900">{formatCurrency(project.budget)}</p></div>
          <div><p className="text-[10px] text-gray-400">Spent</p><p className="text-sm font-bold text-gray-900" data-testid="project-total-costs-consumed">{formatCurrency(project.total_costs_consumed ?? project.total_spent)}</p><p className="text-[9px] text-gray-300">consumed</p></div>
          <div><p className="text-[10px] text-gray-400">Start</p><p className="text-sm font-bold text-gray-900">{formatDate(project.start_date)}</p></div>
          <div><p className="text-[10px] text-gray-400">End</p><p className="text-sm font-bold text-gray-900">{formatDate(project.end_date)}</p></div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-100 overflow-x-auto">
        {TABS.map(tab => (
          <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
            <tab.icon className="w-3.5 h-3.5" />{tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        {activeTab === 'overview' && (
          <div className="space-y-4">
            {project.description && <div><p className="text-xs font-medium text-gray-400 mb-1">Description</p><p className="text-sm text-gray-700">{project.description}</p></div>}
            <div className="grid grid-cols-2 gap-4">
              {project.site_address && <div><p className="text-xs font-medium text-gray-400 mb-1">Site Address</p><p className="text-sm text-gray-700">{project.site_address}</p></div>}
              {project.stretch && <div><p className="text-xs font-medium text-gray-400 mb-1">Stretch / Chainage</p><p className="text-sm text-gray-700">{project.stretch}</p></div>}
              {project.assigned_to && <div><p className="text-xs font-medium text-gray-400 mb-1">Assigned To</p><p className="text-sm text-gray-700">{project.assigned_to}</p></div>}
              <div><p className="text-xs font-medium text-gray-400 mb-1">Created By</p><p className="text-sm text-gray-700">{project.created_by_name || '-'}</p></div>
            </div>
            {/* Phase 1 — show estimation inputs if any are set */}
            {(project.estimated_work > 0 || project.rate_per_unit > 0 || project.labour_rate_per_unit > 0 || project.avg_material_consumption_per_unit > 0) && (
              <div className="pt-3 border-t border-gray-50">
                <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Estimation</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="p-2 bg-blue-50 rounded-lg"><p className="text-[10px] text-blue-500">Est. Work</p><p className="text-sm font-bold text-blue-700" data-testid="overview-estimated-work">{(project.estimated_work || 0).toLocaleString('en-IN')} {project.estimated_work_unit || ''}</p></div>
                  <div className="p-2 bg-emerald-50 rounded-lg"><p className="text-[10px] text-emerald-600">Rate / Unit</p><p className="text-sm font-bold text-emerald-700">{formatCurrency(project.rate_per_unit || 0)}</p></div>
                  <div className="p-2 bg-amber-50 rounded-lg"><p className="text-[10px] text-amber-600">Labour / Unit</p><p className="text-sm font-bold text-amber-700">{formatCurrency(project.labour_rate_per_unit || 0)}</p></div>
                  <div className="p-2 bg-indigo-50 rounded-lg"><p className="text-[10px] text-indigo-500">Avg Material / Unit</p><p className="text-sm font-bold text-indigo-700">{(project.avg_material_consumption_per_unit || 0).toLocaleString('en-IN')}</p></div>
                </div>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-50">
              <div className="text-center p-3 bg-blue-50 rounded-lg"><p className="text-lg font-bold text-blue-700">{(project.work_items || []).length}</p><p className="text-[10px] text-blue-500">Work Items</p></div>
              <div className="text-center p-3 bg-amber-50 rounded-lg"><p className="text-lg font-bold text-amber-700">{(project.milestones || []).length}</p><p className="text-[10px] text-amber-500">Service Invoices</p></div>
              <div className="text-center p-3 bg-emerald-50 rounded-lg"><p className="text-lg font-bold text-emerald-700">{(project.photos || []).length}</p><p className="text-[10px] text-emerald-500">Photos</p></div>
            </div>

            {/* Phase 4 — P&L analytics */}
            <ProjectAnalytics projectId={project.id} />
          </div>
        )}
        {activeTab === 'materials' && <MaterialCostsTab project={project} />}
        {activeTab === 'work-items' && <WorkDetailsTab project={project} />}
        {activeTab === 'service-invoice' && <ServiceInvoiceTab project={project} />}
        {activeTab === 'labour-invoice' && <LabourInvoiceTab project={project} />}
        {activeTab === 'transportation' && <TransportationTab project={project} />}
        {activeTab === 'expenses' && <ExpensesTab project={project} onRefresh={loadProject} />}
        {activeTab === 'photos' && <PhotosTab project={project} onRefresh={loadProject} />}
      </div>
    </div>
  );
};

// ============ MAIN MODULE ============
const SiteProjectModule = () => {
  const [projects, setProjects] = useState([]);
  const [stats, setStats] = useState({ total: 0, planning: 0, in_progress: 0, on_hold: 0, completed: 0, total_budget: 0, total_spent: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [view, setView] = useState('list');
  const [selectedId, setSelectedId] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const handlePageSizeChange = (v) => { setPageSize(v); setPage(1); };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const [result, statsRes] = await Promise.all([fetchPaged('/site-projects/portfolio-overview', params), siteProjectAPI.getStats()]);
      setProjects(result.items);
      setTotal(result.total);
      setTotalPages(result.total_pages);
      setStats(statsRes);
    } catch (err) { console.error('Failed to load projects:', err); }
    setLoading(false);
  }, [search, statusFilter, page, pageSize]);

  useEffect(() => { loadData(); customerAPI.getAll().then(d => setCustomers(unwrapItems(d))); }, [loadData]);

  const handleCreate = async (form) => {
    await siteProjectAPI.create(form);
    setView('list');
    loadData();
  };

  if (view === 'detail' && selectedId) {
    return <ProjectDetail projectId={selectedId} onBack={() => { setView('list'); setSelectedId(null); loadData(); }} />;
  }

  if (view === 'create') {
    return (
      <div className="p-6">
        <ProjectForm onSave={handleCreate} onCancel={() => setView('list')} customers={customers} />
      </div>
    );
  }

  return (
    <div data-testid="site-projects-module" className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Site Projects</h1>
          <p className="text-xs text-gray-400 mt-0.5">Manage construction & installation projects</p>
        </div>
        <button data-testid="new-project-btn" onClick={() => setView('create')} className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm hover:shadow-md transition-all" style={{ backgroundColor: '#24549c' }}>
          <Plus className="w-4 h-4" />New Project
        </button>
      </div>

      <StatsBar stats={stats} />

      {/* Search & Filter */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input data-testid="project-search-input" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Search projects..." className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200" />
        </div>
        <select data-testid="project-status-filter" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} className="px-3 py-2 text-sm border border-gray-200 rounded-lg">
          <option value="">All Status</option>
          {['Planning', 'In Progress', 'On Hold', 'Completed'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {/* Project List */}
      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" /></div>
      ) : projects.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <HardHat className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-500">No projects found</p>
          <p className="text-xs text-gray-400 mt-1">Create your first site project to get started</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {projects.map(p => {
            const sc = STATUS_CONFIG[p.status] || STATUS_CONFIG['Planning'];
            const StatusIcon = sc.icon;
            return (
              <button key={p.id} data-testid={`project-card-${p.id}`} onClick={() => { setSelectedId(p.id); setView('detail'); }}
                className="w-full text-left bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md hover:border-gray-200 transition-all group">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {p.project_code && <span className="px-1.5 py-0.5 text-[10px] font-mono font-bold bg-blue-50 text-blue-700 rounded border border-blue-100">{p.project_code}</span>}
                      <h3 className="text-sm font-bold text-gray-900 truncate">{p.project_name}</h3>
                      <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${sc.color}`}><StatusIcon className="w-3 h-3 inline mr-0.5" />{p.status}</span>
                      {p.project_type && <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-500 rounded">{p.project_type}</span>}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-400">
                      {p.customer_name && <span><Users className="w-3 h-3 inline mr-1" />{p.customer_name}</span>}
                      {p.location && <span><MapPin className="w-3 h-3 inline mr-1" />{p.location}</span>}
                      {p.start_date && <span><Calendar className="w-3 h-3 inline mr-1" />{formatDate(p.start_date)}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0 ml-4">
                    <div className="text-right">
                      <p className="text-xs font-bold text-gray-900">{formatCurrency(p.budget)}</p>
                      <p className="text-[10px] text-gray-400">budget</p>
                    </div>
                    <div className="w-16">
                      <div className="flex items-center justify-between mb-0.5"><span className="text-[10px] font-bold" style={{ color: '#24549c' }}>{p.completion}%</span></div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5"><div className="h-1.5 rounded-full" style={{ width: `${p.completion}%`, backgroundColor: '#24549c' }} /></div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                  </div>
                </div>
                <PortfolioKpiStrip project={p} />
              </button>
            );
          })}
        </div>
      )}

      {projects.length > 0 && <Pagination page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={handlePageSizeChange} />}
    </div>
  );
};

export default SiteProjectModule;
